import React, { useState, useEffect, useCallback } from 'react';
import '../styles/learn.css';
import { supabase } from '../integrations/client.ts';
import { Lock, CheckCircle, XmarkCircle, Folder } from 'iconoir-react';
import { IoMdRefresh } from 'react-icons/io';

function Chapters({ chaptersState, onClose, currentCourse, currentUser, isAuthenticated, onSectionSelect, selectedSection, onNavigateToInquiry, onNavigateToLanding, courseData, loading, error, progressRefreshTrigger, onChapterProjectSelect, onRefreshChapters }) {
    const [expandedChapters, setExpandedChapters] = useState({});
    const [userProgress, setUserProgress] = useState({});
    const [chapterProgress, setChapterProgress] = useState({});
    const [loadingProgress, setLoadingProgress] = useState(false);

    // Determine if user is premium
    const isPremium = currentUser?.subscription_type === 'premium';

    // Fetch user progress for all sections and chapter progress
    const fetchUserProgress = useCallback(async () => {
        if (!currentUser || !currentCourse || !courseData?.chapters) {
            setUserProgress({});
            setChapterProgress({});
            return;
        }

        setLoadingProgress(true);
        try {
            // Fetch section progress
            const { data, error } = await supabase
                .from('user_progress')
                .select('section_id, unlocked, quiz_completed, quiz_score')
                .eq('user_id', currentUser.id)
                .eq('course_id', currentCourse.id);

            if (!error && data) {
                const progressMap = {};
                data.forEach(progress => {
                    progressMap[progress.section_id] = {
                        isUnlocked: progress.unlocked,
                        quizCompleted: progress.quiz_completed,
                        quizScore: progress.quiz_score
                    };
                });
                setUserProgress(progressMap);
            }

            // Fetch chapter progress for projects
            const { data: chapterData, error: chapterError } = await supabase
                .from('user_chapter_progress')
                .select('chapter_id, all_quizzes_completed, project_unlocked, project_viewed, project_marked_complete')
                .eq('user_id', currentUser.id)
                .eq('course_id', currentCourse.id);

            if (!chapterError && chapterData) {
                const chapterProgressMap = {};
                chapterData.forEach(progress => {
                    chapterProgressMap[progress.chapter_id] = {
                        allQuizzesCompleted: progress.all_quizzes_completed,
                        projectUnlocked: progress.project_unlocked,
                        projectViewed: progress.project_viewed,
                        projectMarkedComplete: progress.project_marked_complete
                    };
                });
                setChapterProgress(chapterProgressMap);
            }
        } catch (err) {
        } finally {
            setLoadingProgress(false);
        }
    }, [currentUser, currentCourse, courseData, progressRefreshTrigger]);

    useEffect(() => {
        fetchUserProgress();
    }, [fetchUserProgress]);

    if (chaptersState === 'hidden') return null;

    const toggleChapter = (chapterId) => {
        setExpandedChapters(prev => ({
            ...prev,
            [chapterId]: !prev[chapterId]
        }));
    };

    const handleSectionClick = (section, chapterName, chapterId, isUnlocked) => {
        // Only allow clicking on unlocked sections
        if (!isUnlocked) {
            return;
        }

        if (onSectionSelect) {
            onSectionSelect({
                ...section,
                chapter_name: chapterName,
                chapter_id: chapterId
            });
        }
    };

    const getSectionStatus = (section) => {
        const progress = userProgress[section.id];

        // Section is unlocked only if progress shows it's unlocked
        // Content generation is separate from unlock status
        const isUnlocked = progress?.isUnlocked || false;

        // Determine icon based on quiz completion and score
        let icon = null;
        if (!isUnlocked) {
            icon = 'lock';
        } else if (progress?.quizCompleted) {
            // Quiz is completed - check if passed (60% or higher)
            const passed = (progress?.quizScore || 0) >= 60;
            icon = passed ? 'check' : 'fail';
        }

        return {
            isUnlocked,
            quizCompleted: progress?.quizCompleted || false,
            quizScore: progress?.quizScore || null,
            quizPassed: (progress?.quizScore || 0) >= 60,
            icon
        };
    };

    const handleNavigationClick = () => {
        onClose(); // Close chapters panel first
        setTimeout(() => {
            // Navigate based on user tier
            if (isPremium && onNavigateToInquiry) {
                onNavigateToInquiry();
            } else if (onNavigateToLanding) {
                onNavigateToLanding();
            }
        }, 300); // Wait for animation to complete
    };

    const handleRefresh = () => {
        if (onRefreshChapters) {
            onRefreshChapters();
        }
    };

    // Use courseData from props instead of fake data
    const chaptersData = courseData?.chapters || [];

    return (
        <div className={`chapters-container ${chaptersState}`}>
            <div className="chapters-panel">
                <div className="chapters-header">
                    <div className="chapters-title-wrapper">
                        <h2>Chapters</h2>
                        <button className="refresh-btn" onClick={handleRefresh} title="Refresh chapters">
                            <IoMdRefresh />
                        </button>
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="chapters-content">
                    {!isAuthenticated ? (
                        <div className="no-content-message" style={{
                            textAlign: 'center',
                            color: '#666',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            Please sign in to view chapters
                        </div>
                    ) : !currentCourse ? (
                        <div className="no-content-message" style={{
                            textAlign: 'center',
                            color: '#666',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            Select a course from Course History to view chapters
                        </div>
                    ) : currentCourse.template_deleted ? (
                        <div className="no-content-message" style={{
                            textAlign: 'center',
                            padding: '20px'
                        }}>
                            <div style={{
                                color: '#e74c3c',
                                fontSize: '18px',
                                fontWeight: '500',
                                marginBottom: '12px'
                            }}>
                                Course No Longer Available
                            </div>
                            <div style={{
                                color: '#666',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                maxWidth: '400px',
                                margin: '0 auto'
                            }}>
                                This course was created from a template that has been removed from the system.
                                The course content is no longer accessible.
                            </div>
                            <div style={{
                                marginTop: '20px',
                                fontSize: '13px',
                                color: '#999'
                            }}>
                                You can safely delete this course from your history.
                            </div>
                        </div>
                    ) : loading ? (
                        <div style={{ padding: '20px' }}>
                            {/* Skeleton loading for chapters */}
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} style={{ marginBottom: '16px' }}>
                                    {/* Chapter header skeleton */}
                                    <div style={{
                                        padding: '16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{
                                            width: '50%',
                                            height: '18px',
                                            backgroundColor: '#e0e0e0',
                                            borderRadius: '4px',
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            animationDelay: `${i * 0.1}s`
                                        }} />
                                    </div>
                                    {/* Section skeletons */}
                                    {[1, 2, 3].map((j) => (
                                        <div key={j} style={{
                                            marginLeft: '16px',
                                            marginBottom: '8px',
                                            padding: '12px',
                                            backgroundColor: '#fafafa',
                                            borderRadius: '6px'
                                        }}>
                                            <div style={{
                                                width: '60%',
                                                height: '14px',
                                                backgroundColor: '#e0e0e0',
                                                borderRadius: '4px',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                                animationDelay: `${i * 0.1 + j * 0.05}s`
                                            }} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="no-content-message" style={{
                            textAlign: 'center',
                            color: '#e74c3c',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            {error}
                        </div>
                    ) : chaptersData.length === 0 ? (
                        <div className="no-content-message" style={{
                            textAlign: 'center',
                            color: '#666',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            No chapters available for this course
                        </div>
                    ) : (
                        chaptersData.map(chapter => (
                            <div key={chapter.id} className="chapter-item">
                                <div 
                                    className="chapter-title"
                                    onClick={() => toggleChapter(chapter.id)}
                                >
                                    <span className={`chapter-arrow ${expandedChapters[chapter.id] ? 'expanded' : ''}`}>
                                        ▶
                                    </span>
                                    {chapter.name}
                                </div>
                                {expandedChapters[chapter.id] && (
                                    <div className="chapter-sections">
                                        {chapter.sections.map((section) => {
                                            const status = getSectionStatus(section);
                                            return (
                                                <div
                                                    key={section.id}
                                                    className={`section-item ${selectedSection?.id === section.id ? 'selected' : ''} ${!status.isUnlocked ? 'locked' : ''}`}
                                                    onClick={status.isUnlocked ? () => handleSectionClick(section, chapter.name, chapter.id, status.isUnlocked) : undefined}
                                                    style={{
                                                        backgroundColor: selectedSection?.id === section.id
                                                            ? '#e3f2fd'
                                                            : !status.isUnlocked
                                                            ? '#f5f5f5'
                                                            : undefined,
                                                        border: selectedSection?.id === section.id ? '2px solid #2196f3' : undefined,
                                                        borderRadius: selectedSection?.id === section.id ? '4px' : undefined,
                                                        fontWeight: selectedSection?.id === section.id ? '500' : undefined,
                                                        opacity: !status.isUnlocked ? 0.5 : 1,
                                                        cursor: !status.isUnlocked ? 'not-allowed' : 'pointer',
                                                        pointerEvents: !status.isUnlocked ? 'none' : 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    <span style={{ flex: 1 }}>
                                                        {section.name}
                                                        {selectedSection?.id === section.id && (
                                                            <span style={{
                                                                marginLeft: '8px',
                                                                color: '#2196f3',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                ← Current
                                                            </span>
                                                        )}
                                                    </span>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginLeft: '8px'
                                                    }}>
                                                        {status.icon === 'lock' && (
                                                            <Lock
                                                                width={14}
                                                                height={14}
                                                                strokeWidth={2}
                                                                color="#999"
                                                            />
                                                        )}
                                                        {status.icon === 'check' && (
                                                            <CheckCircle
                                                                width={14}
                                                                height={14}
                                                                strokeWidth={2}
                                                                color="#4caf50"
                                                            />
                                                        )}
                                                        {status.icon === 'fail' && (
                                                            <XmarkCircle
                                                                width={14}
                                                                height={14}
                                                                strokeWidth={2}
                                                                color="#f44336"
                                                            />
                                                        )}
                                                        {status.quizCompleted && status.quizScore !== null && (
                                                            <span style={{
                                                                fontSize: '11px',
                                                                color: status.quizPassed ? '#1b5e20' : '#b71c1c',
                                                                backgroundColor: status.quizPassed ? '#e8f5e9' : '#ffebee',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontWeight: '500'
                                                            }}>
                                                                {Math.round(status.quizScore)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Chapter Project Item - Only show for skill-based courses that are NOT from templates */}
                                        {currentCourse?.is_skill_based && !currentCourse?.is_from_template && (() => {
                                            const progress = chapterProgress[chapter.id];
                                            const isUnlocked = progress?.projectUnlocked || false;
                                            const isComplete = progress?.projectMarkedComplete || false;

                                            return (
                                                <div
                                                    className={`section-item chapter-project-item ${!isUnlocked ? 'locked' : ''}`}
                                                    onClick={isUnlocked && onChapterProjectSelect ? () => onChapterProjectSelect(chapter) : undefined}
                                                    style={{
                                                        backgroundColor: '#fff3e0',
                                                        border: '1px dashed #ff9800',
                                                        marginTop: '8px',
                                                        opacity: !isUnlocked ? 0.5 : 1,
                                                        cursor: !isUnlocked ? 'not-allowed' : 'pointer',
                                                        pointerEvents: !isUnlocked ? 'none' : 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Folder
                                                            width={16}
                                                            height={16}
                                                            strokeWidth={2}
                                                            color="#e65100"
                                                        />
                                                        <span style={{ fontStyle: 'italic', color: '#e65100' }}>
                                                            Chapter Project
                                                        </span>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginLeft: '8px'
                                                    }}>
                                                        {!isUnlocked && (
                                                            <Lock
                                                                width={14}
                                                                height={14}
                                                                strokeWidth={2}
                                                                color="#999"
                                                            />
                                                        )}
                                                        {isComplete && (
                                                            <CheckCircle
                                                                width={14}
                                                                height={14}
                                                                strokeWidth={2}
                                                                color="#4caf50"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
                
                {/* Navigation button at the bottom */}
                <div className="chapters-footer" style={{
                    padding: '16px 24px',
                    backgroundColor: 'transparent'
                }}>
                    <button 
                        onClick={handleNavigationClick}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            backgroundColor: '#333333',
                            color: '#f8f9fa',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#333333';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#333333';
                        }}
                    >
                        <span>←</span>
                        <span>{isPremium ? 'Back to Inquiry' : 'Learn Something Else'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Chapters;